import React, { useState } from 'react';
import { GetJobLogs_getJobLogs_details as JobLogDetailsType } from '../../../types/schemaTypes';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
import { OverlayTrigger, Table } from 'react-bootstrap';
import moment from 'moment';

interface JobLogDetailsItemProps {
    jobLogsData: JobLogDetailsType;
    handleOnStopLog: (jobLogId: any) => void;
    handleOnDeleteLog: (jobLogId: any) => void;
    onClickEdit: () => void;
}

const JobLogDetailsItem = ({
    jobLogsData,
    handleOnStopLog,
    handleOnDeleteLog,
    onClickEdit,
}: JobLogDetailsItemProps): React.ReactElement => {
    const authContext = useAuthContext();
    const isAddJobLogsEnabled = authContext.userHasPermission(PermissionEnum.AddJobLogs);
    const isEditJobLogsEnabled = authContext.userHasPermission(PermissionEnum.EditJobLogs);
    const isEditResolvedJobLogsEnabled = authContext.userHasPermission(PermissionEnum.EditResolvedJobLogs);
    const isDeleteJobLogsEnabled = authContext.userHasPermission(PermissionEnum.DeleteJobLogs);

    const [showMore, setShowMore] = useState<boolean>(false);

    const timeZone = moment.tz(moment.tz.guess()).zoneAbbr();

    // Get fullNotes array
    const fullNotes = (jobLogsData as any).fullNotes || [];
    
    return (
        <>
            <tr className="JobLog-Details-Row">
                {jobLogsData.jobLogTypeId === 5 ||
                (isEditJobLogsEnabled && jobLogsData.status !== 'Resolved') ||
                isEditResolvedJobLogsEnabled ? (
                    <td className="JobLogs-Edit-Link" onClick={onClickEdit}>
                        {jobLogsData.id}
                    </td>
                ) : (
                    <td>{jobLogsData.id}</td>
                )}
                <td>{jobLogsData.jobLogType}</td>
                <td>{jobLogsData.jobLogIssue}</td>
                {jobLogsData.jobLogTypeId === 5 ? (
                    <td>
                        {JSON.parse(jobLogsData?.notes ?? '').Notes === ' . '
                            ? ''
                            : JSON.parse(jobLogsData?.notes ?? '').Notes}
                    </td>
                ) : fullNotes.length > 0 ? (
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 250, hide: 400 }}
                        overlay={
                            <div className="Inventory-Tooltip">
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th>Notes</th>
                                            <th>Created By</th>
                                            <th>Created Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fullNotes.map((noteItem: any) => (
                                            <tr key={noteItem.id}>
                                                <td>{noteItem.notes || '-'}</td>
                                                <td>{noteItem.createdBy || '-'}</td>
                                                <td>{noteItem.dateCreated ? `${noteItem.dateCreated} (${timeZone})` : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        }
                    >
                        <td className="JobLogs-Notes">
                            {fullNotes.map((noteItem: any, index: number) => (
                                <div key={noteItem.id || index}>{noteItem.notes}</div>
                            ))}
                        </td>
                    </OverlayTrigger>
                ) : (
                    <td></td>
                )}
                <td>
                    {jobLogsData.startDate} ({timeZone})
                </td>
                <td>{jobLogsData.endDate ? `${jobLogsData.endDate} (${timeZone})` : ''}</td>
                <td>{jobLogsData.duration}</td>
                <td>
                    <div className="JobLogs-Details-Buttons">
                        <div className="JobLog-Delete-Stop-Button-Container">
                            {isDeleteJobLogsEnabled && jobLogsData.jobLogTypeId !== 5 && (
                                <span className="JobLog-Delete-Button" onClick={() => handleOnDeleteLog(jobLogsData.id)}>
                                    Delete
                                </span>
                            )}
                            {[1, 2, 3, 6].includes(jobLogsData.jobLogTypeId ?? 0) && !jobLogsData.endDate && (
                                <span
                                    className="JobLog-Stop-Button"
                                    onClick={() => handleOnStopLog(jobLogsData.id)}
                                >
                                    Stop
                                </span>
                            )}
                        </div>

                        {[1, 2, 3, 4, 5, 6].includes(jobLogsData.jobLogTypeId ?? 0) ? (
                            showMore ? (
                                <div className="JobLogs-MoreLessContainer" onClick={() => setShowMore(false)}>
                                    <span className="JobLogs-ViewMoreLess">Less</span>
                                    <i className="material-icons JobLogs-ViewMoreLessButton">expand_less</i>
                                </div>
                            ) : (
                                <div className="JobLogs-MoreLessContainer" onClick={() => setShowMore(true)}>
                                    <span className="JobLogs-ViewMoreLess">More</span>
                                    <i className="material-icons JobLogs-ViewMoreLessButton">expand_more</i>
                                </div>
                            )
                        ) : (
                            ' '
                        )}
                    </div>
                </td>
            </tr>

            {showMore && (
                <tr className="JobLog-Item-Container">
                    {jobLogsData.jobLogTypeId === 5 ? (
                        <td colSpan={8}>
                            <div className="JobLog-Item-Row">
                                <div className="JobLog-Item">
                                    <p className="JobLog-Item-Label">Stage:</p>
                                    <p className="JobLog-Item-Info">{jobLogsData.stage ?? ''}</p>
                                </div>
                                <div className="JobLog-Item">
                                    <p className="JobLog-Item-Label">Overall Stage:</p>
                                    <p className="JobLog-Item-Info">{jobLogsData.overallStage ?? ''}</p>
                                </div>
                                <div className="JobLog-Item">
                                    <p className="JobLog-Item-Label">
                                        {jobLogsData.jobLogIssue && jobLogsData.jobLogIssue == 'Start Stage'
                                            ? 'Estimated Sand Consumption:'
                                            : 'Total Volume Pumped:'}
                                    </p>
                                    <p className="JobLog-Item-Info">{jobLogsData.totalSandPumped ?? ''}</p>
                                </div>
                            </div>
                        </td>
                    ) : (
                        <td colSpan={8}>
                            <div className="JobLog-Item-Row">
                                {jobLogsData.dateCreated ? (
                                    <div className="JobLog-Item">
                                        <p className="JobLog-Item-Label">Created On:</p>
                                        <p className="JobLog-Item-Info">
                                            {jobLogsData.dateCreated} ({timeZone})
                                        </p>
                                    </div>
                                ) : (
                                    <></>
                                )}
                                {jobLogsData.createdBy ? (
                                    <div className="JobLog-Item">
                                        <p className="JobLog-Item-Label">Created By:</p>
                                        <p className="JobLog-Item-Info">{jobLogsData.createdBy}</p>
                                    </div>
                                ) : (
                                    <></>
                                )}
                                {jobLogsData.status ? (
                                    <div className="JobLog-Item">
                                        <p className="JobLog-Item-Label">Status:</p>
                                        <p className="JobLog-Item-Info">{jobLogsData.status}</p>
                                    </div>
                                ) : (
                                    <></>
                                )}
                                {jobLogsData.amount ? (
                                    <div className="JobLog-Item">
                                        <p className="JobLog-Item-Label">Amount (in lbs):</p>
                                        <p className="JobLog-Item-Info">{jobLogsData.amount}</p>
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>

                            <div className="JobLog-Item-Row">
                                {/* {jobLogsData.wellName ? (
                                    <div className="JobLog-Item">
                                        <p className="JobLog-Item-Label">Wellhead:</p>
                                        <p className="JobLog-Item-Info">{jobLogsData.wellName}</p>
                                    </div>
                                ) : (
                                    <></>
                                )} */}
                                {jobLogsData.estimatedDowntime ? (
                                    <div className="JobLog-Item">
                                        <p className="JobLog-Item-Label">Estimated Downtime (in minutes):</p>
                                        <p className="JobLog-Item-Info">{jobLogsData.estimatedDowntime}</p>
                                    </div>
                                ) : (
                                    <></>
                                )}
                                {jobLogsData.stage ? (
                                    <div className="JobLog-Item">
                                        <p className="JobLog-Item-Label">Stage No:</p>
                                        <p className="JobLog-Item-Info">{jobLogsData.stage}</p>
                                    </div>
                                ) : (
                                    <></>
                                )}
                                {jobLogsData.loadId ? (
                                    <div className="JobLog-Item">
                                        <p className="JobLog-Item-Label">Load ID:</p>
                                        <p className="JobLog-Item-Info">{jobLogsData.loadId}</p>
                                    </div>
                                ) : (
                                    <></>
                                )}
                                {jobLogsData.lofa ? (
                                    <div className="JobLog-Item">
                                        <p className="JobLog-Item-Label">Location/ Lofa:</p>
                                        <p className="JobLog-Item-Info">{jobLogsData.lofa}</p>
                                    </div>
                                ) : (
                                    <></>
                                )}
                                {jobLogsData.rootCause ? (
                                    <div className="JobLog-Item">
                                        <p className="JobLog-Item-Label">Root Cause:</p>
                                        <p className="JobLog-Item-Info">{jobLogsData.rootCause}</p>
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>

                            <div className="JobLog-Item-Row-Lg">
                                {jobLogsData.correctiveAction ? (
                                    <div className="JobLog-Item">
                                        <p className="JobLog-Item-Label">Corrective Action:</p>
                                        <p className="JobLog-Item-Info">{jobLogsData.correctiveAction}</p>
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>
                        </td>
                    )}
                </tr>
            )}
        </>
    );
};

export default JobLogDetailsItem;
